// Componente para input de chat (versión web).
// Maneja su propio estado de loading internamente para ser autónomo.
// Usa hooks para envío de mensajes.
// Adaptado para web desde amarsi-app (React Native a React DOM).

import React, { useState, useCallback, useRef } from 'react';
import { useAuthQuery } from '../../hooks/useAuthQuery';
import { useSendMessage } from '../../hooks/chat/useSendMessage';
import { useToast } from '../../hooks/useToast';
import { chatService } from '../../services/chatService';
import { FiSend, FiMapPin, FiImage } from 'react-icons/fi';
import EnviarUbi from './EnviarUbi';
import ImageSelector from './ImageSelector';

// Agregado prop onComplete para compatibilidad con el componente legacy.
// onComplete se llama después de enviar el mensaje exitosamente con el chatroomId.
export default function InputChatBox({ 
  destinatario, 
  maxLength = 300, 
  onPress, 
  onComplete, 
  actionComponent, 
  messageText, 
  setMessageText, 
  textInputRef,
  multiline = false 
}) {
  // Estado local para chatroomId, loading y teclado.
  const [chatroomId, setChatroomId] = useState(null);
  const [internalLoading, setInternalLoading] = useState(false);
  
  // Estados para modales de adjuntos
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);

  // Hooks externos.
  const { sessionQuery } = useAuthQuery();
  const session = sessionQuery.data?.session;
  const user = session?.user;
  const sendMessageMutation = useSendMessage();
  const { showError } = useToast({ position: 'top-right' });
  const internalRef = useRef(null);
  const currentRef = textInputRef || internalRef;

  // Si messageText no se pasa, usa estado interno (para FriendProfileScreen).
  const [internalMessageText, setInternalMessageText] = useState('');
  const currentMessageText = messageText !== undefined ? messageText : internalMessageText;
  const currentSetMessageText = setMessageText || setInternalMessageText;

  const canSend = currentMessageText ? currentMessageText.trim().length > 0 : false;

  // Función para enviar mensaje.
  // Siempre maneja el loading internamente para ser autónomo.
  const sendMessage = useCallback(async () => {
    setInternalLoading(true);
    
    try {
      if (onPress) {
        await onPress();
      } else {
        // Lógica antigua si no se pasa onPress
        const messageBody = currentMessageText.trim();

        if (!user || messageBody.length <= 3 || sendMessageMutation.isLoading) {
          showError("Mensaje muy corto o usuario no válido.");
          return;
        }

        // Obtiene o crea chatroomId solo si no existe, al enviar mensaje.
        let currentChatroomId = chatroomId;
        if (!currentChatroomId) {
          try {
            currentChatroomId = await chatService.getOrCreateChatroomId(user.id, destinatario.friend_id);
            setChatroomId(currentChatroomId);
          } catch (error) {
            showError(error.message);
            return;
          }
        }

        // Construye objeto mensaje.
        const mensajito = {
          ahorita: new Date(),
          chatroom_id: currentChatroomId,
          body: messageBody,
          destinatario: destinatario.friend_id,
          msg_type: 1, // texto
        };

        // Usa la mutación para enviar.
        await new Promise((resolve, reject) => {
          sendMessageMutation.mutate(mensajito, {
            onSuccess: () => {
              currentSetMessageText('');
              // Llama a onComplete si existe con el chatroomId
              if (onComplete) {
                onComplete(currentChatroomId);
              }
              resolve();
            },
            onError: reject,
          });
        });
      }
    } catch (error) {
      console.error('Error al enviar mensaje:', error);
      showError(error.message || "Error al enviar mensaje");
    } finally {
      setInternalLoading(false);
    }
  }, [user, currentMessageText, chatroomId, destinatario, sendMessageMutation, onPress, onComplete, currentSetMessageText, showError]);

  // Manejar envío con Enter
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !multiline && canSend) {
      e.preventDefault();
      sendMessage();
    }
  }, [canSend, sendMessage, multiline]);

  // Función para enviar ubicación
  const handleSendLocation = useCallback(async (location) => {
    setInternalLoading(true);
    
    try {
      // Obtiene o crea chatroomId
      let currentChatroomId = chatroomId;
      if (!currentChatroomId) {
        currentChatroomId = await chatService.getOrCreateChatroomId(user.id, destinatario.friend_id);
        setChatroomId(currentChatroomId);
      }

      // Construye objeto mensaje de ubicación
      const mensajito = {
        ahorita: new Date(),
        chatroom_id: currentChatroomId,
        body: JSON.stringify(location),
        destinatario: destinatario.friend_id,
        msg_type: 3, // ubicación
      };

      // Envía el mensaje
      await new Promise((resolve, reject) => {
        sendMessageMutation.mutate(mensajito, {
          onSuccess: () => {
            if (onComplete) {
              onComplete(currentChatroomId);
            }
            resolve();
          },
          onError: reject,
        });
      });
    } catch (error) {
      console.error('Error al enviar ubicación:', error);
      showError(error.message || 'Error al enviar ubicación');
    } finally {
      setInternalLoading(false);
    }
  }, [chatroomId, user, destinatario, sendMessageMutation, onComplete, showError]);

  // Función para enviar imagen
  const handleSendImage = useCallback(async (base64Image) => {
    setInternalLoading(true);
    
    try {
      // Obtiene o crea chatroomId
      let currentChatroomId = chatroomId;
      if (!currentChatroomId) {
        currentChatroomId = await chatService.getOrCreateChatroomId(user.id, destinatario.friend_id);
        setChatroomId(currentChatroomId);
      }

      // Construye objeto mensaje de imagen
      const mensajito = {
        ahorita: new Date(),
        chatroom_id: currentChatroomId,
        body: base64Image,
        destinatario: destinatario.friend_id,
        msg_type: 2, // imagen
      };

      // Envía el mensaje
      await new Promise((resolve, reject) => {
        sendMessageMutation.mutate(mensajito, {
          onSuccess: () => {
            if (onComplete) {
              onComplete(currentChatroomId);
            }
            resolve();
          },
          onError: reject,
        });
      });
    } catch (error) {
      console.error('Error al enviar imagen:', error);
      showError(error.message || 'Error al enviar imagen');
    } finally {
      setInternalLoading(false);
    }
  }, [chatroomId, user, destinatario, sendMessageMutation, onComplete, showError]);

  // Si no hay usuario, no renderiza.
  if (!user?.id) return null;

  // Siempre usa loading interno, sin depender de props externas.
  const currentLoading = internalLoading;

  return (
    <div className="p-4 bg-gradient-to-t from-neutral-900/50 to-transparent border-t border-neutral-700/50 backdrop-blur-sm">
      <div className="flex items-end gap-3 max-w-4xl mx-auto">
        {/* Botones de adjuntos - rediseñados */}
        {/* <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => setShowLocationModal(true)}
            className="p-2.5 bg-neutral-800/50 hover:bg-neutral-700/70 rounded-xl transition-all duration-200 hover:scale-105 active:scale-95 disabled:opacity-50 disabled:hover:scale-100"
            disabled={currentLoading}
            title="Enviar ubicación"
          >
            <FiMapPin size={20} className="text-neutral-200" />
          </button>
          <button
            onClick={() => setShowImageModal(true)}
            className="p-2.5 bg-neutral-800/50 hover:bg-neutral-700/70 rounded-xl transition-all duration-200 hover:scale-105 active:scale-95 disabled:opacity-50 disabled:hover:scale-100"
            disabled={currentLoading}
            title="Enviar imagen"
          >
            <FiImage size={20} className="text-neutral-200" />
          </button>
        </div> */}

        {/* Input de texto rediseñado */}
        <div className="flex-1 relative">
          <div className="flex items-end bg-neutral-800/30 border-2 border-neutral-600/50 rounded-2xl overflow-hidden backdrop-blur-sm transition-all duration-200 focus-within:border-neutral-500/70">
            <input
              ref={currentRef}
              type="text"
              className="flex-1 bg-transparent text-white text-base py-3 px-4 min-h-12 outline-none placeholder-neutral-400/70"
              placeholder="Escribe un mensaje..."
              value={currentMessageText}
              maxLength={maxLength}
              onChange={(e) => currentSetMessageText(e.target.value)}
              disabled={currentLoading}
              onKeyDown={handleKeyDown}
            />

            {canSend && (
              <div className="px-3 pb-2.5 pointer-events-none">
                <span className="text-xs text-neutral-400/80 font-medium">
                  {currentMessageText.length}/{maxLength}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Botón de enviar rediseñado */}
        {!canSend && !currentLoading ? (
           <div className="flex items-center justify-center shrink-0">
             {actionComponent}
           </div>
         ) : (
           <div className="flex items-center justify-center shrink-0">
             <button
               className={`rounded-2xl w-12 h-12 flex items-center justify-center transition-all duration-200 hover:scale-105 active:scale-95 ${
                 canSend 
                   ? 'bg-gradient-to-br from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 ' 
                   : 'bg-neutral-800/50'
               } ${currentLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
               disabled={!canSend || currentLoading}
               onClick={sendMessage}
             >
               {currentLoading ? (
                 <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
               ) : (
                 <FiSend 
                   size={20} 
                   className={canSend ? 'text-white' : 'text-neutral-400'} 
                 />
               )}
             </button>
           </div>
         )}

      </div>

      {/* Modal de ubicación - rediseñado */}
      {showLocationModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <EnviarUbi
            onSendLocation={handleSendLocation}
            onClose={() => setShowLocationModal(false)}
          />
        </div>
      )}

      {/* Modal de imagen - rediseñado */}
      {showImageModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <ImageSelector
            onSendImage={handleSendImage}
            onClose={() => setShowImageModal(false)}
          />
        </div>
      )}
    </div>
  );
}
